// src/features/factors/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

// import factor type options from filter.tsx (do not modify filter.tsx)
import { factorTypeOptions } from "./filter";

// Zod schema: group_name & factor_type are required; tag/tag_id optional
const FormSchema = z.object({
  group_name: z.string().min(1, "Group Name is required"),
  factor_type: z.coerce.number({
    message: "Factor Type is required",
  }),
  // AutocompleteAsync field for Tag (display + ID)
  tag: z.preprocess(
    (val: any) => preprocessTextAsyncAutocompleteField(val),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  tag_id: z.coerce.number().optional().nullable(),
});

export function FactorsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Factors {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Group Name & Factor Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="group_name"
                    label={t(`${table_name}.columns.group_name`, {
                      ns: "tables",
                      defaultValue: "Group Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <SelectField
                    name="factor_type"
                    label={t(`${table_name}.columns.factor_type`, {
                      ns: "tables",
                      defaultValue: "Factor Type",
                    })}
                    options={factorTypeOptions}
                    control={form.control}
                    allow_empty_option={false}
                  />
                </div>
                {/* Row 2: Tag autocomplete */}
                <div className="grid grid-cols-1 gap-4">
                  <AutocompleteAsyncField
                    name="tag"
                    label={t(`${table_name}.columns.tag`, {
                      ns: "tables",
                      defaultValue: "Tag",
                    })}
                    base_url="legacy-quantilia/tags"
                    column_name="name"
                    id_column="tag_id"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation Modal for creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["tag_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel/Back Confirmation Modal */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
